import type {
  AirportView,
  FleetAircraftView,
  RouteAnalyticsView,
  RouteSeatShareView,
} from '../../../domain/models/contracts';
import type { CongestionStatus } from '../common/KpiSummaryStrip';

/**
 * UI-side roll-ups for one hub, from the worker's per-route analytics. Route analytics describe the latest
 * simulated day, so weekly figures are that day times seven.
 */
const DAYS_PER_WEEK = 7;
/** Slot pressure at or above this reads as congested. */
const CONGESTED_AT_PCT = 70;

export interface HubKpis {
  readonly weeklyPassengers: number;
  readonly weeklyMovements: number;
  readonly weeklySeats: number;
  readonly loadFactorPct: number;
  readonly activeRoutes: number;
  readonly connectedAirports: number;
  readonly congestion: CongestionStatus;
  readonly pressurePct: number;
}

export function hubKpis(
  hubId: string,
  routes: readonly RouteAnalyticsView[],
  airport: AirportView | undefined,
  weeklyDepartures: number,
): HubKpis {
  let passengers = 0;
  let seats = 0;
  const neighbours = new Set<string>();
  for (const route of routes) {
    passengers += route.passengers;
    seats += route.seats;
  }
  for (const route of routes) neighbours.add(route.origin === hubId ? route.destination : route.origin);
  const weeklyMovements = weeklyDepartures * 2;
  const dailyCapacity = airport === undefined ? 0 : airport.slotCapacityHourly.reduce((sum, v) => sum + v, 0);
  const pressurePct = dailyCapacity > 0 ? (weeklyMovements / (dailyCapacity * DAYS_PER_WEEK)) * 100 : 0;
  return {
    weeklyPassengers: passengers * DAYS_PER_WEEK,
    weeklyMovements,
    weeklySeats: seats * DAYS_PER_WEEK,
    loadFactorPct: seats > 0 ? (passengers / seats) * 100 : 0,
    activeRoutes: new Set(routes.map((r) => pairKey(r.origin, r.destination))).size,
    connectedAirports: neighbours.size,
    congestion: pressurePct >= CONGESTED_AT_PCT ? 'Congested' : 'Comfortable',
    pressurePct,
  };
}

function pairKey(a: string, b: string): string {
  return a < b ? `${a}-${b}` : `${b}-${a}`;
}

export interface TrafficLeader {
  readonly rank: number;
  readonly otherAirportId: string;
  readonly name: string;
  readonly city: string;
  readonly flightsPerWeek: number;
  readonly airlines: number;
}

export function trafficLeaders(
  hubId: string,
  routes: readonly RouteAnalyticsView[],
  airportsById: Readonly<Record<string, AirportView>>,
  shares: readonly RouteSeatShareView[],
  limit: number,
): readonly TrafficLeader[] {
  const flights = new Map<string, number>();
  for (const route of routes) {
    const other = route.origin === hubId ? route.destination : route.origin;
    flights.set(other, (flights.get(other) ?? 0) + route.operatedFlights * DAYS_PER_WEEK);
  }
  return [...flights.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([other, flightsPerWeek], index) => {
      const share = shares.find((s) => pairKey(s.originId, s.destinationId) === pairKey(hubId, other));
      const airport = airportsById[other];
      return {
        rank: index + 1,
        otherAirportId: other,
        name: airport?.name ?? other,
        city: airport?.city ?? '',
        flightsPerWeek,
        airlines: Math.max(1, share?.shares.length ?? 1),
      };
    });
}

export interface AircraftMixRow {
  readonly typeName: string;
  readonly flightsPerWeek: number;
  readonly sharePct: number;
}

export function aircraftMix(
  routes: readonly RouteAnalyticsView[],
  fleet: readonly FleetAircraftView[],
  typeNames: Readonly<Record<string, string>>,
): readonly AircraftMixRow[] {
  const typeByReg = new Map(fleet.map((a) => [a.registration, a.typeId] as const));
  const flights = new Map<string, number>();
  let total = 0;
  for (const route of routes) {
    const typeId = typeByReg.get(route.aircraftReg);
    if (typeId === undefined) continue;
    const name = typeNames[typeId] ?? typeId;
    const weekly = route.operatedFlights * DAYS_PER_WEEK;
    flights.set(name, (flights.get(name) ?? 0) + weekly);
    total += weekly;
  }
  return [...flights.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([typeName, flightsPerWeek]) => ({ typeName, flightsPerWeek, sharePct: total > 0 ? (flightsPerWeek / total) * 100 : 0 }));
}

export interface PerformanceBreakdown {
  readonly scheduled: number;
  readonly onTimePct: number;
  readonly minorPct: number;
  readonly moderatePct: number;
  readonly majorPct: number;
}

/**
 * The simulation does not model delays yet, so every operated flight counts as on time and cancellations are the
 * only disruption. The minor and moderate bands stay at zero until delay data exists.
 */
export function weeklyPerformance(routes: readonly RouteAnalyticsView[]): PerformanceBreakdown {
  let scheduled = 0;
  let cancelled = 0;
  for (const route of routes) {
    scheduled += route.scheduledFlights;
    cancelled += route.cancelledFlights;
  }
  const majorPct = scheduled > 0 ? (cancelled / scheduled) * 100 : 0;
  return {
    scheduled: scheduled * DAYS_PER_WEEK,
    onTimePct: scheduled > 0 ? 100 - majorPct : 0,
    minorPct: 0,
    moderatePct: 0,
    majorPct,
  };
}
